/**
 * ListView Component
 *
 * Displays documents in a table format with sortable columns and keyboard navigation.
 *
 * @module features/dashboard/components/ListView
 * @see SPEC-016 Requirements 3.1, 3.2, 3.3, 3.4, 3.7
 * @see SPEC-017 Task 6.2 - i18n integration
 */

import React, { useCallback, useRef, useState } from 'react';
import { cn } from '../../../lib/cn';
import { useTypedTranslation } from '../../../i18n';
import { formatSmartDate, formatDateTime } from '../../../i18n/formatters';
import type { SupportedDateLocale } from '../../../i18n/formatters';
import type { DocListItemInput } from '@linkode/shared';
import type { SortBy, SortOrder } from '../store/dashboardStore';

// ============================================================================
// Types
// ============================================================================

export interface ListViewProps {
  /** Documents to display */
  docs: DocListItemInput[];
  /** Current sort field */
  sortBy: SortBy;
  /** Current sort order */
  sortOrder: SortOrder;
  /** Selected document IDs */
  selectedDocIds: string[];
  /** Sort change handler */
  onSortChange: (sortBy: SortBy, sortOrder: SortOrder) => void;
  /** Document click handler */
  onDocClick: (doc: DocListItemInput) => void;
  /** Document context menu handler */
  onDocContextMenu: (doc: DocListItemInput, event: React.MouseEvent) => void;
  /** Document selection toggle handler */
  onDocSelect: (docId: string) => void;
  /** Document delete handler */
  onDocDelete: (docId: string) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Column Definitions
// ============================================================================

interface ColumnDef {
  id: SortBy | 'actions';
  label: string;
  sortable: boolean;
  width: string;
  align: 'left' | 'center' | 'right';
}

const columns: ColumnDef[] = [
  { id: 'title', label: 'Title', sortable: true, width: 'flex-1', align: 'left' },
  { id: 'updatedAt', label: 'Updated', sortable: true, width: 'w-32', align: 'left' },
  { id: 'createdAt', label: 'Created', sortable: true, width: 'w-32', align: 'left' },
  { id: 'actions', label: '', sortable: false, width: 'w-20', align: 'center' },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get locale from i18n language code
 */
function getLocaleFromLanguage(language: string): SupportedDateLocale {
  if (language === 'zh-TW' || language.startsWith('zh')) {
    return 'zh-TW';
  }
  return 'en';
}

// ============================================================================
// Sort Icon Component
// ============================================================================

interface SortIconProps {
  active: boolean;
  direction: SortOrder;
}

const SortIcon: React.FC<SortIconProps> = ({ active, direction }) => (
  <svg
    className={cn(
      'w-4 h-4 ml-1 inline-block transition-transform',
      active ? 'text-linkode-text' : 'text-linkode-text/30',
      active && direction === 'asc' && 'rotate-180'
    )}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

/**
 * ListView Component
 *
 * Features:
 * - Table format with columns: title, updated date, created date
 * - Sortable columns with visual indicators
 * - Keyboard navigation (arrow keys, Enter to open, Delete to delete)
 * - Row hover highlighting
 * - Right-click context menu support
 * - Row selection support
 *
 * @example
 * ```tsx
 * <ListView
 *   docs={documents}
 *   sortBy="updatedAt"
 *   sortOrder="desc"
 *   selectedDocIds={[]}
 *   onSortChange={(field, order) => handleSort(field, order)}
 *   onDocClick={(doc) => navigate(`/editor/${doc.docId}`)}
 *   onDocContextMenu={(doc, e) => showContextMenu(e, doc)}
 *   onDocSelect={(docId) => toggleSelection(docId)}
 *   onDocDelete={(docId) => deleteDoc(docId)}
 * />
 * ```
 *
 * @see Requirements 3.1, 3.2, 3.3, 3.4, 3.7
 */
export const ListView: React.FC<ListViewProps> = ({
  docs,
  sortBy,
  sortOrder,
  selectedDocIds,
  onSortChange,
  onDocClick,
  onDocContextMenu,
  onDocSelect,
  onDocDelete,
  isLoading = false,
  className,
}) => {
  const { t, i18n } = useTypedTranslation();
  const locale = getLocaleFromLanguage(i18n.language);
  const tableRef = useRef<HTMLTableElement>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  // Handle column header click for sorting
  const handleHeaderClick = useCallback(
    (columnId: SortBy) => {
      if (columnId === sortBy) {
        // Toggle sort order
        onSortChange(columnId, sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        // New column, default to descending
        onSortChange(columnId, 'desc');
      }
    },
    [sortBy, sortOrder, onSortChange]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (docs.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, docs.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < docs.length) {
            const doc = docs[focusedIndex];
            if (doc) onDocClick(doc);
          }
          break;
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < docs.length) {
            const doc = docs[focusedIndex];
            if (doc) onDocDelete(doc.docId);
          }
          break;
        case ' ':
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < docs.length) {
            const doc = docs[focusedIndex];
            if (doc) onDocSelect(doc.docId);
          }
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(docs.length - 1);
          break;
      }
    },
    [docs, focusedIndex, onDocClick, onDocDelete, onDocSelect]
  );

  // Handle row click
  const handleRowClick = useCallback(
    (doc: DocListItemInput, index: number) => {
      setFocusedIndex(index);
      onDocClick(doc);
    },
    [onDocClick]
  );

  // Handle row context menu
  const handleRowContextMenu = useCallback(
    (doc: DocListItemInput, index: number, event: React.MouseEvent) => {
      setFocusedIndex(index);
      onDocContextMenu(doc, event);
    },
    [onDocContextMenu]
  );

  return (
    <div className={cn('overflow-x-auto', className)} role="region" aria-label="Document list">
      <table
        ref={tableRef}
        className="w-full border-collapse"
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-busy={isLoading}
      >
        {/* Table Header */}
        <thead>
          <tr className="border-b-2 border-linkode-text">
            {columns.map((column) => (
              <th
                key={column.id}
                className={cn(
                  'px-4 py-3 font-mono font-bold text-sm text-linkode-text uppercase',
                  column.width,
                  column.align === 'left' && 'text-left',
                  column.align === 'center' && 'text-center',
                  column.align === 'right' && 'text-right',
                  column.sortable && 'cursor-pointer select-none hover:bg-linkode-bg-light'
                )}
                onClick={() => column.sortable && handleHeaderClick(column.id as SortBy)}
                aria-sort={
                  column.sortable && column.id === sortBy
                    ? sortOrder === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                {column.label}
                {column.sortable && (
                  <SortIcon active={column.id === sortBy} direction={sortOrder} />
                )}
              </th>
            ))}
          </tr>
        </thead>

        {/* Table Body */}
        <tbody>
          {docs.map((doc, index) => {
            const isSelected = selectedDocIds.includes(doc.docId);
            const isFocused = focusedIndex === index;

            return (
              <tr
                key={doc.docId}
                className={cn(
                  'border-b border-linkode-text/20',
                  'transition-colors duration-150',
                  'cursor-pointer',
                  isSelected && 'bg-linkode-bg-orange',
                  isFocused && !isSelected && 'bg-linkode-bg-light',
                  !isSelected && !isFocused && 'hover:bg-linkode-bg-light'
                )}
                onClick={() => handleRowClick(doc, index)}
                onContextMenu={(e) => handleRowContextMenu(doc, index, e)}
                tabIndex={-1}
                role="row"
                aria-selected={isSelected}
              >
                {/* Title Column */}
                <td className="px-4 py-3 flex-1">
                  <div className="flex items-center gap-2">
                    {/* Document Icon */}
                    <svg
                      className="w-5 h-5 text-linkode-text/50 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="font-mono font-medium text-linkode-text truncate">
                      {doc.title || 'Untitled'}
                    </span>
                    {doc.isShared && (
                      <span className="text-xs text-linkode-teal font-mono px-1.5 py-0.5 bg-linkode-teal/10 rounded">
                        {t('dashboard.listView.sharedBadge')}
                      </span>
                    )}
                  </div>
                </td>

                {/* Updated Date Column */}
                <td className="px-4 py-3 w-32">
                  <time
                    className="text-sm text-linkode-text/70 font-mono"
                    dateTime={new Date(doc.updatedAt).toISOString()}
                    title={formatDateTime(new Date(doc.updatedAt), locale)}
                  >
                    {formatSmartDate(new Date(doc.updatedAt), locale)}
                  </time>
                </td>

                {/* Created Date Column */}
                <td className="px-4 py-3 w-32">
                  <time
                    className="text-sm text-linkode-text/70 font-mono"
                    dateTime={new Date(doc.createdAt).toISOString()}
                    title={formatDateTime(new Date(doc.createdAt), locale)}
                  >
                    {formatSmartDate(new Date(doc.createdAt), locale)}
                  </time>
                </td>

                {/* Actions Column */}
                <td className="px-4 py-3 w-20 text-center">
                  <button
                    type="button"
                    className={cn(
                      'p-1.5 rounded-linkode',
                      'text-linkode-text/50 hover:text-linkode-text',
                      'hover:bg-linkode-bg-light',
                      'transition-colors duration-150',
                      'focus:outline-none focus:ring-2 focus:ring-linkode-teal'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDocContextMenu(doc, e);
                    }}
                    aria-label="More actions"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-linkode-text border-t-transparent" />
        </div>
      )}
    </div>
  );
};
